// Reporting display-currency scoping — PURE.
//
// The reporting page has a lightweight DISPLAY-currency toggle (not the query
// filter): /summary is already per-(date, currency), so picking a currency
// re-derives the WHOLE view (hero cards + mini-charts + big chart) from that
// currency's rows only — instant, no refetch. This is required for correctness:
// a ratio (CPL, cost-per-meta-lead, link CTR) must divide numerator and
// denominator in the SAME currency, so EVERY metric (counts included) is scoped,
// not just money. Documented consequence: hero numbers become per-currency (e.g.
// "leads" = the selected currency's leads), never an all-currencies blend.
//
// `scopeSummaryToCurrency` returns a normal single-currency ReportingSummary, so
// the existing buildHeroCards + buildTrendSeries consume it unchanged.

import type { CurrencyAmount, ReportingSummary, ReportingTrendPoint } from '../../types/reporting';
import type { FxRateSnapshot } from '../../types/fxRates';
import { isNativeDisplay, toDisplayCurrency } from '../../lib/reporting/currencyConversion';

const r2 = (n: number): number => Math.round(n * 100) / 100;
const ratio = (num: number, den: number): number => (den > 0 ? num / den : 0);

// The amount for `currency` in a per-currency array, or 0 if absent.
function amt(arr: CurrencyAmount[] | undefined, currency: string): number {
  return arr?.find((c) => c.currency === currency)?.amount ?? 0;
}

// The toggle is shown only when >1 currency is in scope; a single-currency scope
// (the common single-account/brand case) just renders that currency.
export function showCurrencyToggle(summary: ReportingSummary): boolean {
  return summary.currencies.length > 1;
}

// Default display currency = the one with the most total spend in scope. Falls
// back to the first currency present if there's no spend, and null if there's no
// data at all.
export function defaultDisplayCurrency(summary: ReportingSummary): string | null {
  let best: string | null = null;
  let bestAmount = -Infinity;
  for (const s of summary.hero.spend) {
    if (s.amount > bestAmount) {
      bestAmount = s.amount;
      best = s.currency;
    }
  }
  if (best !== null) return best;
  return summary.currencies.length > 0 ? summary.currencies[0]! : null;
}

// Resolve the active display currency: keep the user's pick while it's still in
// scope, otherwise fall back to the default (so changing filters can't leave a
// stale currency selected). null when there's no data.
export function resolveDisplayCurrency(
  summary: ReportingSummary,
  picked: string | null | undefined,
): string | null {
  if (picked && summary.currencies.includes(picked)) return picked;
  return defaultDisplayCurrency(summary);
}

// Re-derive a single-currency ReportingSummary from the full per-currency summary.
// Every metric is taken from `currency`'s rows only; ratios are recomputed from
// that currency's summed numerator/denominator. The result is a NORMAL summary
// (currencies = [currency]) that buildHeroCards / buildTrendSeries treat as the
// ordinary single-currency case.
export function scopeSummaryToCurrency(
  summary: ReportingSummary,
  currency: string,
): ReportingSummary {
  let spend = 0;
  let metaLeadSpend = 0;
  let leads = 0;
  let metaLeads = 0;
  let websiteLeads = 0;
  let impressions = 0;
  let linkClicks = 0;

  const trend: ReportingTrendPoint[] = summary.trend.map((pt) => {
    const s = amt(pt.spend, currency);
    const mls = amt(pt.metaLeadSpend, currency);
    const l = amt(pt.leadsByCurrency, currency);
    const ml = amt(pt.metaLeadsByCurrency, currency);
    const wl = amt(pt.websiteLeadsByCurrency, currency);
    const imp = amt(pt.impressionsByCurrency, currency);
    const clk = amt(pt.linkClicksByCurrency, currency);
    spend += s;
    metaLeadSpend += mls;
    leads += l;
    metaLeads += ml;
    websiteLeads += wl;
    impressions += imp;
    linkClicks += clk;
    return {
      date: pt.date,
      spend: [{ currency, amount: r2(s) }],
      metaLeadSpend: [{ currency, amount: r2(mls) }],
      leads: l,
      metaLeads: ml,
      websiteLeads: wl,
      impressions: imp,
      linkClicks: clk,
      leadsByCurrency: [{ currency, amount: l }],
      metaLeadsByCurrency: [{ currency, amount: ml }],
      websiteLeadsByCurrency: [{ currency, amount: wl }],
      impressionsByCurrency: [{ currency, amount: imp }],
      linkClicksByCurrency: [{ currency, amount: clk }],
    };
  });

  return {
    range: summary.range,
    currencies: [currency],
    hero: {
      spend: [{ currency, amount: r2(spend) }],
      leads,
      metaLeads,
      websiteLeads,
      cpl: [{ currency, amount: r2(ratio(spend, leads)) }],
      costPerMetaLead: [{ currency, amount: r2(ratio(metaLeadSpend, metaLeads)) }],
      impressions,
      ctr: r2(ratio(linkClicks, impressions) * 100),
    },
    trend,
    asOf: summary.asOf,
  };
}

// ---------------------------------------------------------------------------
// The AGENCY display-currency view — convert-and-blend
// ---------------------------------------------------------------------------

/**
 * Re-derive the summary with every money figure CONVERTED into one display
 * currency and blended.
 *
 * ⚠️ THIS IS THE SIBLING OF `scopeSummaryToCurrency`, NOT A REPLACEMENT, and the
 * difference is the whole point:
 *
 *   · SCOPING (Native mode) answers "what did the AUD book do" — every metric,
 *     counts included, comes from that currency's rows only, so a ratio divides a
 *     numerator and denominator in the same currency. Its documented cost is that
 *     the counts are per-currency too: "leads" means the AUD book's leads.
 *   · CONVERTING (display-currency mode) answers "what did the WHOLE portfolio
 *     do, expressed in AUD" — money is converted at the snapshot rate and summed
 *     across currencies, and the counts are the ALL-CURRENCY totals, because a
 *     lead is a lead whichever currency paid for it.
 *
 * Both return an ordinary single-currency `ReportingSummary`, so `buildHeroCards`
 * / `buildTrendSeries` / every widget consume either unchanged — which is what
 * keeps the ratio recomputation (summed numerator ÷ summed denominator) correct
 * in both modes with no second implementation.
 *
 * ⚠️ An amount with no usable rate is EXCLUDED rather than folded in raw — see
 * `toDisplayCurrency`. That understates a converted total rather than fabricating
 * one, and the missing-rate case is surfaced by the selector (which only offers
 * currencies the table can reach).
 */
export function convertSummaryToDisplay(
  summary: ReportingSummary,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
): ReportingSummary {
  const convert = (amounts: CurrencyAmount[] | undefined): number =>
    toDisplayCurrency(amounts ?? [], display, snapshot)?.amount ?? 0;

  let spend = 0;
  let metaLeadSpend = 0;
  let leads = 0;
  let metaLeads = 0;
  let websiteLeads = 0;
  let impressions = 0;
  let linkClicks = 0;

  const trend: ReportingTrendPoint[] = summary.trend.map((pt) => {
    const s = convert(pt.spend);
    const mls = convert(pt.metaLeadSpend);
    // ⚠️ The SCALAR count fields, not the per-currency ones: in display mode the
    // whole portfolio is in scope, so a bucket's leads are all of that day's
    // leads rather than one currency's.
    spend += s;
    metaLeadSpend += mls;
    leads += pt.leads;
    metaLeads += pt.metaLeads;
    websiteLeads += pt.websiteLeads;
    impressions += pt.impressions;
    linkClicks += pt.linkClicks;
    return {
      date: pt.date,
      spend: [{ currency: display, amount: r2(s) }],
      metaLeadSpend: [{ currency: display, amount: r2(mls) }],
      leads: pt.leads,
      metaLeads: pt.metaLeads,
      websiteLeads: pt.websiteLeads,
      impressions: pt.impressions,
      linkClicks: pt.linkClicks,
      leadsByCurrency: [{ currency: display, amount: pt.leads }],
      metaLeadsByCurrency: [{ currency: display, amount: pt.metaLeads }],
      websiteLeadsByCurrency: [{ currency: display, amount: pt.websiteLeads }],
      impressionsByCurrency: [{ currency: display, amount: pt.impressions }],
      linkClicksByCurrency: [{ currency: display, amount: pt.linkClicks }],
    };
  });

  return {
    range: summary.range,
    currencies: [display],
    hero: {
      spend: [{ currency: display, amount: r2(spend) }],
      leads,
      metaLeads,
      websiteLeads,
      // Ratios recompute from the CONVERTED numerator and the all-currency
      // denominator — the same summed-over-summed rule as everywhere else.
      cpl: [{ currency: display, amount: r2(ratio(spend, leads)) }],
      costPerMetaLead: [{ currency: display, amount: r2(ratio(metaLeadSpend, metaLeads)) }],
      impressions,
      ctr: r2(ratio(linkClicks, impressions) * 100),
    },
    trend,
    asOf: summary.asOf,
  };
}

/**
 * The ONE place the page decides how to collapse a multi-currency summary.
 *
 * Native → scope to one currency's rows (unchanged behaviour). A display
 * currency → convert and blend. Keeping the branch here rather than in the page
 * means the two modes can't drift apart about which one is in force.
 */
export function resolveSummaryView(
  summary: ReportingSummary,
  display: string,
  snapshot: FxRateSnapshot | null | undefined,
  nativeCurrency: string | null,
): ReportingSummary | null {
  if (!isNativeDisplay(display)) return convertSummaryToDisplay(summary, display, snapshot);
  return nativeCurrency ? scopeSummaryToCurrency(summary, nativeCurrency) : null;
}
